import { figmaPxToWidthPercent } from '@ui/Report';

export type MethodologySection = {
  heading: string;
  paragraphs: string[];
};

export const METHODOLOGY_TITLE = 'Methodology';

/** Static method description shown as the last page of the report (DEV-3560). */
export const METHODOLOGY_SECTIONS: readonly MethodologySection[] = [
  {
    heading: 'Scope of the assessment',
    paragraphs: [
      'The carbon assessment covers the building elements and life cycle modules specified in the assessment scope of the report.',
      'The scope also states the purpose of the assessment and the requirements or guidelines applied. These may include the Swedish climate declaration, Miljöbyggnad, BREEAM, or project- and company-specific carbon assessment guidelines.',
    ],
  },
  {
    heading: 'Assessment method',
    paragraphs: [
      'The assessment is based on the Swedish National Board of Housing, Building and Planning’s guidelines for carbon assessments and climate declarations.',
      'When the assessment covers additional stages of the building’s life cycle, the method is supplemented by IVL’s guidelines for whole-life carbon assessment. This may include relevant modules in life cycle stages A, B and C, as specified in the assessment scope.',
    ],
  },
  {
    heading: 'Quantities',
    paragraphs: [
      'Quantities are obtained from the project documentation, such as IFC models, DWG files, PDF drawings, cost estimates and other project documents.',
      'The quantities are linked to building elements, products and climate data. The data is presented in a resource summary that can be exported for review and further analysis.',
    ],
  },
  {
    heading: 'Climate data',
    paragraphs: [
      'Climate data is primarily obtained from third-party-verified Environmental Product Declarations (EPDs) that comply with EN 15804+A2. For EPDs, the GWP-GHG indicator is used.',
      'When a suitable EPD is unavailable, climate data from the Swedish National Board of Housing, Building and Planning’s climate database, the Finnish emissions database for construction, or another source specified in the resource summary is used.',
      'For Swedish climate declarations, climate data and calculation methods are applied in accordance with the guidelines of the Swedish National Board of Housing, Building and Planning. For other assessments, other data types may be used when required by the specified assessment scope.',
      'The selected climate data and data sources are stated in the resource summary.',
    ],
  },
  {
    heading: 'Transport and construction',
    paragraphs: [
      'For module A4, project-specific transport distances and modes of transport are used when available. When this information is unavailable, transport values and calculation methods from the Swedish National Board of Housing, Building and Planning are used.',
      'For module A5, project-specific data on material waste and the use of energy and fuels is used when available. Otherwise, values and calculation methods from the Swedish National Board of Housing, Building and Planning or IVL are applied, depending on the scope of the report.',
    ],
  },
  {
    heading: 'Generic assumptions',
    paragraphs: [
      'For building elements where detailed quantities are unavailable, generic assumptions based on IVL’s guidelines may be used. This may include building services, internal finishes and other elements that have not yet been designed in detail.',
      'Other established or project-specific assumptions may also be used. The assumptions and their data sources are stated in the resource summary.',
    ],
  },
  {
    heading: 'Traceability',
    paragraphs: [
      'The report and resource summary present the quantities, climate data, generic assumptions and system boundaries used in the assessment. They also show which parts of the assessment are based on project-specific information and which are based on standardised values.',
    ],
  },
];

/** Typography for the methodology columns — scaling numbers, not px. */
export const METHODOLOGY_TEXT = {
  columnCount: 3,
  columnGap: figmaPxToWidthPercent(64),
  sectionGap: figmaPxToWidthPercent(24),
  paragraphGap: figmaPxToWidthPercent(10),
  headingFontSize: figmaPxToWidthPercent(18),
  bodyFontSize: figmaPxToWidthPercent(14),
  lineHeight: 1.4,
  color: '#282a31',
} as const;
